/**
 * The editor's client id, kept for the life of a browser tab.
 *
 * The server hands a lease back to the client id that took it, so a reload
 * reclaims the lease its last page held instead of meeting it as somebody
 * else's until it expires. A duplicated tab copies session storage, so the id
 * is marked live while a page holds it, and a copy found live mints its own.
 *
 * @module platform/tab-client-id
 */

import { uid } from "@/utils/uid";

const STORAGE_KEY = "socratiq.editor-client-id";

let pageClientId: string | undefined;

function write(id: string, live: boolean): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ id, live }));
  } catch {
    // Storage refused: the id still holds for this page, only not past it.
  }
}

function readReleased(): string | undefined {
  try {
    const stored: unknown = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) ?? "null");
    if (
      stored &&
      typeof stored === "object" &&
      "id" in stored &&
      typeof stored.id === "string" &&
      !("live" in stored && stored.live)
    ) {
      return stored.id;
    }
  } catch {
    // Unreadable is the same as absent.
  }
  return undefined;
}

/** This tab's editor client id, the same for every editor on the page. */
export function tabClientId(): string {
  if (pageClientId) {
    return pageClientId;
  }
  const id = readReleased() ?? uid("programme-editor");
  pageClientId = id;
  write(id, true);
  window.addEventListener("pagehide", () => write(id, false));
  // Back from the back-forward cache, the page holds the id again.
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      write(id, true);
    }
  });
  return id;
}
